import { API_BASE_URL } from '../config/env';
import { isAuthSessionError, isSubscriptionRequiredError, notifySessionExpired } from '../utils/authSession';
const inFlightGetRequests = new Map();
const getResponseCache = new Map();
const GET_CACHE_TTL_MS = 1500;

function getErrorMessage(data, status) {
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.join(', ');
  }
  if (data?.message) {
    return data.message;
  }
  return `API error: ${status}`;
}

export function clearGetCache(endpoint) {
  const cacheKey = `GET:${endpoint}`;
  inFlightGetRequests.delete(cacheKey);
  getResponseCache.delete(cacheKey);
}

export function clearAllGetCache() {
  inFlightGetRequests.clear();
  getResponseCache.clear();
}

export async function apiRequest(endpoint, options = {}) {
  const { headers: optionHeaders, allowNotFound = false, ...rest } = options;
  const method = (rest.method || 'GET').toUpperCase();
  const cacheKey = method === 'GET' ? `${method}:${endpoint}` : null;

  if (cacheKey) {
    const cached = getResponseCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }

    if (inFlightGetRequests.has(cacheKey)) {
      return inFlightGetRequests.get(cacheKey);
    }
  }

  const requestPromise = (async () => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...rest,
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        ...optionHeaders,
      },
    });

    if (!response.ok) {
      let errorMessage = `API error: ${response.status}`;
      try {
        const data = await response.json();
        errorMessage = getErrorMessage(data, response.status);
      } catch {
        // ignore parse errors
      }

      if (isAuthSessionError(response.status, errorMessage)) {
        notifySessionExpired();
        return { success: false, message: errorMessage, data: null };
      }

      if (allowNotFound && response.status === 404) {
        const result = { success: false, message: errorMessage, data: null };
        if (cacheKey) {
          getResponseCache.set(cacheKey, {
            data: result,
            expiresAt: Date.now() + GET_CACHE_TTL_MS,
          });
        }
        return result;
      }

      if (isSubscriptionRequiredError(errorMessage)) {
        const result = { success: false, message: errorMessage, data: null };
        if (cacheKey) {
          getResponseCache.set(cacheKey, {
            data: result,
            expiresAt: Date.now() + GET_CACHE_TTL_MS,
          });
        }
        return result;
      }

      throw new Error(errorMessage);
    }

    if (response.status === 204) return null;

    let data = null;
    try {
      data = await response.json();
    } catch {
      if (cacheKey) {
        getResponseCache.set(cacheKey, {
          data: null,
          expiresAt: Date.now() + GET_CACHE_TTL_MS,
        });
      }
      return null;
    }

    if (data?.success === false) {
      const errorMessage = getErrorMessage(data, response.status);

      if (isAuthSessionError(response.status, errorMessage)) {
        notifySessionExpired();
        return { success: false, message: errorMessage, data: null };
      }

      if (allowNotFound) {
        if (cacheKey) {
          getResponseCache.set(cacheKey, {
            data,
            expiresAt: Date.now() + GET_CACHE_TTL_MS,
          });
        }
        return data;
      }

      if (isSubscriptionRequiredError(errorMessage)) {
        if (cacheKey) {
          getResponseCache.set(cacheKey, {
            data,
            expiresAt: Date.now() + GET_CACHE_TTL_MS,
          });
        }
        return data;
      }
    }

    if (cacheKey) {
      getResponseCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + GET_CACHE_TTL_MS,
      });
    }
    return data;
  })();

  if (cacheKey) {
    inFlightGetRequests.set(cacheKey, requestPromise);
    requestPromise.finally(() => {
      inFlightGetRequests.delete(cacheKey);
    });
  }

  return requestPromise;
}
